// ProductCreateWizard — the catalog's DELIBERATE birth door (the products-catalog
// surface; ADR-065). The inline door in the asset flow stays skinny; THIS is where a
// product is described properly, in the architect's ruled shape:
//
//   Identity (category · name · maker · series · part number · the category's
//   spec fields · the networked switch + can-front)
//     → Capabilities & interfaces (ONLY when networked — the step is not in
//       the Stepper array otherwise: the rail renumbers, no skip ceremony)
//     → Summary (the review before the create).
//
// The component is the REAL page body (title · caption · card · Stepper — the
// asset-wizard frame); it mounts under FocusedLayout at the route, and the
// Workbench mounts it inside FocusedFrame for judging. Data crosses only
// through props: `onSubmit` receives the normalized wire body.

import { useEffect, useMemo, useRef, useState } from 'react';
import { Controller, type UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import {
  DEVICE_CAPABILITY_GROUPS,
  normalizeCapabilities,
  type DeviceCapability,
  type DeviceCapabilityGroup,
  type ModelInterfaces,
} from '@ems-app/schemas/assets/access';
import { TECHNOLOGY_CHEMISTRY, type BatteryChemistry, type BatteryTechnology } from '@ems-app/schemas/common/battery';
import {
  ASSET_CATEGORY_DESCRIPTORS,
  PRODUCT_CATEGORIES,
  frontPostureOf,
  isConnectivityNa,
  type AssetCategory,
  type ModelSpecColumn,
} from '@ems-app/schemas/assets/categories';
import type { CreateAssetModelRequest } from '@ems-app/schemas/catalog/requests';
import { CATEGORY_ICON, CATEGORY_SECTIONS } from '../../components/patterns/assetsDisplay.js';
import { Picker } from '../../components/patterns/Picker.js';
import { WizardReview, type WizardReviewRow, type WizardReviewSection } from '../../components/patterns/WizardReview.js';
import type { UseFormShellReturn } from '../../components/patterns/forms/useFormShell.js';
import { RefusalLine } from '../../components/patterns/forms/RefusalLine.js';
import { Checkbox } from '../../components/ui/Checkbox.js';
import { ChoiceTiles, type ChoiceTileGroup } from '../../components/ui/ChoiceTiles.js';
import { Button } from '../../components/ui/Button.js';
import { Input } from '../../components/ui/Input.js';
import { Label } from '../../components/ui/Label.js';
import { NumberInput } from '../../components/ui/NumberInput.js';
import { Stepper, type StepperStep } from '../../components/ui/Stepper.js';
import { Textarea } from '../../components/ui/Textarea.js';
import { Toggle } from '../../components/ui/Toggle.js';
import { formatList, formatNumber } from '../../lib/format.js';
import { useAssetVocabularies } from '../../components/patterns/assetFormShared.js';

const sectionTitle = 'text-base font-semibold text-[var(--color-text-primary)]';

// ── form model ───────────────────────────────────────────────────────────────

export const ProductFormSchema = z.object({
  category: z.string().min(1),
  name: z.string().trim().min(1).max(96),
  manufacturer: z.string().trim().max(64),
  series: z.string().trim().max(48),
  partNumber: z.string().trim().max(64),
  description: z.string().trim().max(2000),
  specPowerKw: z.number().nullable(),
  specCapacityKva: z.number().nullable(),
  specCapacityAh: z.number().nullable(),
  specVoltageV: z.number().nullable(),
  specWattPeakW: z.number().nullable(),
  specCoolingKw: z.number().nullable(),
  specTankL: z.number().nullable(),
  specBatteryChem: z.string().nullable(),
  specBatteryTech: z.string().nullable(),
  warrantyMonths: z.number().nullable(),
  networked: z.boolean(),
  fronts: z.boolean(),
  capabilities: z.array(z.string()),
  sims: z.number().nullable(),
  esim: z.boolean(),
  ethernetPorts: z.number().nullable(),
  ethernetWan: z.number().nullable(),
  rs232: z.number().nullable(),
  rs485: z.number().nullable(),
});
export type ProductFormValues = z.infer<typeof ProductFormSchema>;

export const EMPTY_PRODUCT_FORM: ProductFormValues = {
  category: '',
  name: '',
  manufacturer: '',
  series: '',
  partNumber: '',
  description: '',
  specPowerKw: null,
  specCapacityKva: null,
  specCapacityAh: null,
  specVoltageV: null,
  specWattPeakW: null,
  specCoolingKw: null,
  specTankL: null,
  specBatteryChem: null,
  specBatteryTech: null,
  warrantyMonths: null,
  networked: false,
  fronts: false,
  capabilities: [],
  sims: null,
  esim: false,
  ethernetPorts: null,
  ethernetWan: null,
  rs232: null,
  rs485: null,
};

const SPEC_FIELD: Record<ModelSpecColumn, { name: keyof ProductFormValues; decimals?: 1 | 2 }> = {
  power_kw: { name: 'specPowerKw', decimals: 2 },
  capacity_kva: { name: 'specCapacityKva', decimals: 2 },
  capacity_ah: { name: 'specCapacityAh' },
  voltage_v: { name: 'specVoltageV', decimals: 1 },
  watt_peak_w: { name: 'specWattPeakW' },
  cooling_kw: { name: 'specCoolingKw', decimals: 2 },
  tank_l: { name: 'specTankL' },
  battery_chemistry: { name: 'specBatteryChem' },
  battery_technology: { name: 'specBatteryTech' },
};

/** The category's descriptor-legal specs from the form values (the server
 *  re-enforces the same law). */
export function buildSpecBody(v: ProductFormValues): Partial<Pick<CreateAssetModelRequest, ModelSpecColumn>> {
  const cat = v.category as AssetCategory;
  const legal = new Set<ModelSpecColumn>(ASSET_CATEGORY_DESCRIPTORS[cat].modelSpecColumns);
  const specs: Partial<Pick<CreateAssetModelRequest, ModelSpecColumn>> = {};
  const putNum = (col: Exclude<ModelSpecColumn, 'battery_chemistry' | 'battery_technology'>, val: number | null) => {
    if (legal.has(col) && val !== null && val > 0) specs[col] = val;
  };
  putNum('power_kw', v.specPowerKw);
  putNum('capacity_kva', v.specCapacityKva);
  putNum('capacity_ah', v.specCapacityAh);
  putNum('voltage_v', v.specVoltageV);
  putNum('watt_peak_w', v.specWattPeakW);
  putNum('cooling_kw', v.specCoolingKw);
  putNum('tank_l', v.specTankL);
  if (legal.has('battery_chemistry') && v.specBatteryChem) {
    specs.battery_chemistry = v.specBatteryChem as CreateAssetModelRequest['battery_chemistry'];
  }
  if (legal.has('battery_technology') && v.specBatteryTech) {
    specs.battery_technology = v.specBatteryTech as CreateAssetModelRequest['battery_technology'];
  }
  return specs;
}

/** The device half from the form values — registry order + the absence law
 *  (an update sends both replace-whole; empty = declared-nothing). */
export function buildDeviceHalf(v: ProductFormValues): { capabilities: DeviceCapability[]; interfaces: ModelInterfaces } {
  const capabilities = v.networked ? normalizeCapabilities(v.capabilities as DeviceCapability[]) : [];
  const iface: ModelInterfaces = {};
  if (v.networked) {
    if (v.sims != null && v.sims > 0) iface.sims = Math.min(Math.round(v.sims), 4);
    if (v.esim) iface.esim = true;
    const ports = v.ethernetPorts != null && v.ethernetPorts > 0 ? Math.round(v.ethernetPorts) : null;
    if (ports) {
      iface.ethernet = { ports };
      const wan = v.ethernetWan != null && v.ethernetWan > 0 ? Math.min(Math.round(v.ethernetWan), ports) : null;
      if (wan) iface.ethernet.wan = wan;
    }
    const rs232 = v.rs232 != null && v.rs232 > 0 ? Math.round(v.rs232) : null;
    const rs485 = v.rs485 != null && v.rs485 > 0 ? Math.round(v.rs485) : null;
    if (rs232 || rs485) iface.serial = { ...(rs232 ? { rs232 } : {}), ...(rs485 ? { rs485 } : {}) };
    if (v.fronts) iface.fronts = true;
  }
  return { capabilities, interfaces: iface };
}

/** The normalized wire body (the same laws the server re-enforces: descriptor-
 *  legal specs · registry-ordered capabilities · the absence law · the
 *  inherit-vs-override networked switch). */
export function buildCreateProductBody(v: ProductFormValues): CreateAssetModelRequest {
  const cat = v.category as AssetCategory;
  const specs = buildSpecBody(v);
  const { capabilities, interfaces: iface } = buildDeviceHalf(v);

  return {
    category: cat,
    ...(v.warrantyMonths && v.warrantyMonths > 0 ? { warranty_months: v.warrantyMonths } : {}),
    name: v.name.trim(),
    manufacturer: v.manufacturer.trim() || null,
    ...(v.series.trim() ? { series: v.series.trim() } : {}),
    ...(v.partNumber.trim() ? { part_number: v.partNumber.trim() } : {}),
    ...(v.description.trim() ? { description: v.description.trim() } : {}),
    ...specs,
    // NULL = inherit the charter three-state; an na category has no switch and
    // never sends an override (the verb rejects it — ADR-067 rev).
    ...(!isConnectivityNa(cat) && v.networked !== (ASSET_CATEGORY_DESCRIPTORS[cat].connectivity === 'on')
      ? { is_network_device: v.networked }
      : {}),
    ...(capabilities.length > 0 ? { capabilities } : {}),
    ...(Object.keys(iface).length > 0 ? { interfaces: iface } : {}),
  };
}

/** What a fact of a kind change names: a spec column, or the device facts whole. */
export type ProductKindClear = ModelSpecColumn | 'device';

/** A kind change on the draft — both create wizards alike: what both kinds ask STAYS (the identity · the warranty · the
 *  specs they share · the device facts while the new kind can be networked · an answer the person gave the networked
 *  switch or the fronting tick); a preset follows the new kind; the rest is CLEARED, and the facts that held a value
 *  come back to be said. */
export function changeProductKind(v: ProductFormValues, next: AssetCategory): { values: ProductFormValues; cleared: ProductKindClear[] } {
  const prev = v.category === '' ? null : (v.category as AssetCategory);
  const values: ProductFormValues = { ...v, category: next };
  const cleared: ProductKindClear[] = [];
  const asked = new Set<ModelSpecColumn>(ASSET_CATEGORY_DESCRIPTORS[next].modelSpecColumns);
  for (const col of Object.keys(SPEC_FIELD) as ModelSpecColumn[]) {
    if (asked.has(col)) continue;
    const { name } = SPEC_FIELD[col];
    if (v[name] !== null) cleared.push(col);
    (values as Record<keyof ProductFormValues, unknown>)[name] = null;
  }
  const presetNetworked = (c: AssetCategory) => ASSET_CATEGORY_DESCRIPTORS[c].connectivity === 'on';
  const presetFronts = (c: AssetCategory) => frontPostureOf(c) === 'default-on';
  const frontsAnswered = prev !== null && v.fronts !== presetFronts(prev);
  const described =
    frontsAnswered || v.capabilities.length > 0 || v.sims !== null || v.esim || v.ethernetPorts !== null || v.ethernetWan !== null || v.rs232 !== null || v.rs485 !== null;
  if (isConnectivityNa(next)) {
    // the new kind cannot be networked (its switch is absent) — the device facts go with it
    if (v.networked && described) cleared.push('device');
    Object.assign(values, { networked: false, fronts: false, capabilities: [], sims: null, esim: false, ethernetPorts: null, ethernetWan: null, rs232: null, rs485: null });
  } else {
    const answered = prev !== null && !isConnectivityNa(prev) && (v.networked !== presetNetworked(prev) || described);
    values.networked = answered ? v.networked : presetNetworked(next);
    values.fronts = frontsAnswered ? v.fronts : presetFronts(next);
  }
  return { values, cleared };
}

// ── steps ────────────────────────────────────────────────────────────────────

type F = UseFormReturn<ProductFormValues>;

function IdentityStep({ form, seriesSuggestions }: { form: F; seriesSuggestions: string[] }) {
  const { t } = useTranslation('catalog');
  const category = form.watch('category') as AssetCategory | '';
  const networked = form.watch('networked');
  const Glyph = category ? CATEGORY_ICON[category] : null;
  // "Change" re-opens the grid over the KEPT draft (the current kind ticked): the same tile goes back with nothing
  // lost; another tile keeps what both kinds ask and says what it cleared.
  const [choosing, setChoosing] = useState(false);
  // What the last kind change cleared — said under the specs, kept until the next change; the region exists before
  // its text so it is announced.
  const [notice, setNotice] = useState<string | null>(null);
  const grid = !category || choosing;
  // Focus follows the swap a "Change" opened: onto the ticked tile, then back onto "Change".
  const gridRef = useRef<HTMLFieldSetElement>(null);
  const changeRef = useRef<HTMLButtonElement>(null);
  const refocus = useRef<'grid' | 'change' | null>(null);
  useEffect(() => {
    if (refocus.current === 'grid') gridRef.current?.querySelector<HTMLInputElement>('input:checked')?.focus();
    else if (refocus.current === 'change') changeRef.current?.focus();
    refocus.current = null;
  }, [grid]);

  const specName = (col: ProductKindClear) =>
    col === 'device'
      ? t('wizard.steps.device')
      : col === 'battery_chemistry'
        ? t('assets:form.model.chemistry')
        : col === 'battery_technology'
          ? t('assets:form.model.technology')
          : t(`assets:form.model.spec.${col}`);
  const choose = (c: AssetCategory) => {
    if (choosing) refocus.current = 'change';
    setChoosing(false);
    const v = form.getValues();
    if (c === v.category) return;
    const { values, cleared } = changeProductKind(v, c);
    for (const key of Object.keys(values) as (keyof ProductFormValues)[]) {
      if (values[key] !== v[key]) form.setValue(key, values[key] as never, { shouldDirty: true });
    }
    setNotice(cleared.length > 0 ? t('assets:form.kindCleared', { to: t(`assets:category.${c}`), facts: formatList(cleared.map(specName)) }) : null);
  };

  // The asset wizard's grammar (MERGED step): the category GRID is this step
  // until a tile is picked — SAME sections, same tiles (CATEGORY_SECTIONS);
  // the step then stays put and materializes the product fields under the
  // chosen category's header (glyph + name + Change). Only categories a product
  // may BELONG to (PRODUCT_CATEGORIES): an asset-only category (solar_string —
  // site-built, never a SKU) would birth a product nothing can ever reference.
  const gridOptions: ChoiceTileGroup[] = CATEGORY_SECTIONS.map(([section, cats]) => ({
    label: t(`assets:form.grid.${section}`),
    options: cats
      .filter((c) => PRODUCT_CATEGORIES.includes(c))
      .map((c) => ({ value: c, label: t(`assets:category.${c}`), icon: CATEGORY_ICON[c] })),
  })).filter((g) => g.options.length > 0);

  // The children keep their places across the swap (an absent one is a hole), so the status line is ONE node.
  return (
    <div className="space-y-4">
      {grid ? (
        <fieldset ref={gridRef}>
          <legend className={sectionTitle}>{t('assets:form.whatIsIt')}</legend>
          <ChoiceTiles
            className="mt-3"
            ariaLabel={t('assets:form.whatIsIt')}
            options={gridOptions}
            orientation="vertical"
            gridClassName="grid-cols-2 sm:grid-cols-3 md:grid-cols-4"
            value={category || null}
            onChange={(c) => choose(c as AssetCategory)}
            onReselect={(c) => choose(c as AssetCategory)}
            chooseOnPress
          />
        </fieldset>
      ) : (
        <div className="flex items-center gap-2.5">
          {Glyph && <Glyph size={22} aria-hidden className="text-[var(--color-text-secondary)]" />}
          <h2 className={sectionTitle}>{t(`assets:category.${category}`)}</h2>
          <Button
            ref={changeRef}
            variant="link"
            onClick={() => {
              setNotice(null);
              refocus.current = 'grid';
              setChoosing(true);
            }}
          >
            {t('assets:form.changeCategory')}
          </Button>
        </div>
      )}
      {!grid && (
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <Label htmlFor="pw-name" required>{t('wizard.fields.name')}</Label>
            <Input id="pw-name" dir="ltr" {...form.register('name')} error={form.formState.errors.name ? ' ' : undefined} />
            <p className="mt-1 text-sm text-[var(--color-text-secondary)]">{t('wizard.fields.nameHint')}</p>
          </div>
          <div>
            <Label htmlFor="pw-maker">{t('wizard.fields.manufacturer')}</Label>
            <Input id="pw-maker" dir="ltr" {...form.register('manufacturer')} />
          </div>
          <div>
            <Label htmlFor="pw-series">{t('wizard.fields.series')}</Label>
            <Input id="pw-series" dir="ltr" list="pw-series-options" {...form.register('series')} />
            <datalist id="pw-series-options">
              {seriesSuggestions.map((s) => (
                <option key={s} value={s} />
              ))}
            </datalist>
          </div>
          <div>
            <Label htmlFor="pw-pn">{t('wizard.fields.partNumber')}</Label>
            <Input id="pw-pn" dir="ltr" className="font-mono" {...form.register('partNumber')} />
          </div>
          <div>
            <Label htmlFor="pw-desc">{t('wizard.fields.description')}</Label>
            <Textarea id="pw-desc" rows={3} {...form.register('description')} />
          </div>
        </div>
      )}
      {!grid && <ProductSpecFields form={form} />}
      <p role="status" aria-live="polite" className={notice && !grid ? 'text-sm text-[var(--color-text-secondary)]' : 'sr-only'}>
        {grid ? null : notice}
      </p>
      {/* Every device fact folds behind the networked switch (ADR-067 rev) —
          and the switch itself is structurally ABSENT on a connectivity-na
          category (the charter says the category cannot speak; the verb
          rejects an override). */}
      {!grid && !isConnectivityNa(category) && (
        <div className="space-y-3 border-t border-[var(--color-border)] pt-4">
          <div>
            <Toggle
              checked={networked}
              onChange={(on) => form.setValue('networked', on, { shouldDirty: true })}
              label={t('wizard.fields.networked')}
            />
            <p className="mt-1 text-sm text-[var(--color-text-secondary)]">{t('wizard.fields.networkedHint')}</p>
          </div>
          {networked && (
            <Checkbox
              checked={form.watch('fronts')}
              onChange={(on) => form.setValue('fronts', on, { shouldDirty: true })}
              label={t('wizard.fields.fronts')}
              description={t('wizard.fields.frontsHint')}
            />
          )}
        </div>
      )}
    </div>
  );
}

/** The category's spec fields (descriptor-scoped) + the warranty every product
 *  keeps (months) — shared by the wizard's Identity step and the edit drawer's
 *  Identity & specs facet. */
export function ProductSpecFields({ form }: { form: F }) {
  const { t } = useTranslation('catalog');
  const { t: tAssets } = useTranslation('assets');
  const vocab = useAssetVocabularies(tAssets);
  const category = form.watch('category') as AssetCategory | '';
  const chem = form.watch('specBatteryChem') as BatteryChemistry | null;
  if (!category) return null;
  const specCols = ASSET_CATEGORY_DESCRIPTORS[category].modelSpecColumns;
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      {specCols.map((col) =>
        col === 'battery_chemistry' ? (
          <Controller
            key={col}
            control={form.control}
            name="specBatteryChem"
            render={({ field }) => (
              // a technology outside the stated chemistry can't stand — the technology field's own parent rule clears
              // it and says so (ADR-080 §9)
              <Picker source={vocab.chemistry} label={t('assets:form.model.chemistry')} value={field.value} onChange={(v) => field.onChange(v ?? null)} clearable />
            )}
          />
        ) : col === 'battery_technology' ? (
          <Controller
            key={col}
            control={form.control}
            name="specBatteryTech"
            render={({ field }) => (
              <Picker
                source={vocab.technology}
                // under a chemistry only its technologies are offered; a chemistry change keeps what belongs, says the rest
                dependsOn={{ value: chem, label: chem ? tAssets(`form.model.batteryChem.${chem}`) : undefined, case: 'must_belong' }}
                label={t('assets:form.model.technology')}
                value={field.value}
                onChange={(v) => {
                  field.onChange(v ?? null);
                  // stating a technology states its chemistry (derivable) — an empty parent becoming a value never clears
                  if (v) form.setValue('specBatteryChem', TECHNOLOGY_CHEMISTRY[v as BatteryTechnology], { shouldDirty: true });
                }}
                clearable
              />
            )}
          />
        ) : (
          <div key={col}>
            <Label htmlFor={`pw-spec-${col}`}>{t(`assets:form.model.spec.${col}`)}</Label>
            <Controller
              control={form.control}
              name={SPEC_FIELD[col].name as 'specPowerKw'}
              render={({ field }) => (
                <NumberInput id={`pw-spec-${col}`} value={field.value} onChange={field.onChange} decimals={SPEC_FIELD[col].decimals} />
              )}
            />
          </div>
        ),
      )}
      <div>
        <Label htmlFor="pw-warranty">{t('assets:form.model.warrantyMonths')}</Label>
        <Controller
          control={form.control}
          name="warrantyMonths"
          render={({ field }) => <NumberInput id="pw-warranty" value={field.value} onChange={field.onChange} />}
        />
      </div>
    </div>
  );
}

/** The capabilities & interfaces block — the wizard's conditional step AND the
 *  edit drawer's facet body. Interface counts render as PROGRESSIVE PRESENCE
 *  GROUPS (Cellular · Ethernet · Serial — ADR-067 rev): a bare zero-defaulted
 *  count input cannot express "not evidenced", so a group exists only once
 *  added, and removing it drops its facts (the absence law's UI form). */
export function ProductDeviceFields({ form }: { form: F }) {
  const { t } = useTranslation('catalog');
  const caps = form.watch('capabilities');
  const toggleCap = (token: string, on: boolean) =>
    form.setValue('capabilities', on ? [...caps, token] : caps.filter((c) => c !== token), { shouldDirty: true });
  const countField = (name: 'sims' | 'ethernetPorts' | 'ethernetWan' | 'rs232' | 'rs485', id: string, label: string) => (
    <div>
      <Label htmlFor={id}>{label}</Label>
      <Controller
        control={form.control}
        name={name}
        render={({ field }) => <NumberInput id={id} value={field.value} onChange={field.onChange} />}
      />
    </div>
  );

  // Presence = a group's facts are evidenced (values set) or the curator added
  // it this session; removing clears the facts, so presence re-derives false.
  const v = form.watch();
  const [added, setAdded] = useState<Record<'cellular' | 'ethernet' | 'serial', boolean>>({
    cellular: false,
    ethernet: false,
    serial: false,
  });
  const present = {
    cellular: added.cellular || (v.sims !== null && v.sims > 0) || v.esim,
    ethernet: added.ethernet || (v.ethernetPorts !== null && v.ethernetPorts > 0),
    serial: added.serial || (v.rs232 !== null && v.rs232 > 0) || (v.rs485 !== null && v.rs485 > 0),
  };
  const removeGroup = (g: 'cellular' | 'ethernet' | 'serial') => {
    setAdded((s) => ({ ...s, [g]: false }));
    if (g === 'cellular') {
      form.setValue('sims', null, { shouldDirty: true });
      form.setValue('esim', false, { shouldDirty: true });
    } else if (g === 'ethernet') {
      form.setValue('ethernetPorts', null, { shouldDirty: true });
      form.setValue('ethernetWan', null, { shouldDirty: true });
    } else {
      form.setValue('rs232', null, { shouldDirty: true });
      form.setValue('rs485', null, { shouldDirty: true });
    }
  };
  const group = (g: 'cellular' | 'ethernet' | 'serial', label: string, body: React.ReactNode) =>
    present[g] ? (
      <fieldset className="rounded-lg border border-[var(--color-border)] p-3.5">
        <div className="mb-2 flex items-start justify-between gap-2">
          <legend className="text-sm font-semibold text-[var(--color-text-primary)]">{label}</legend>
          <Button variant="link" tone="neutral" onClick={() => removeGroup(g)}>
            {t('wizard.device.noneEvidenced')}
          </Button>
        </div>
        {body}
      </fieldset>
    ) : (
      <div className="flex items-center justify-between gap-2 rounded-lg border border-dashed border-[var(--color-border)] px-3.5 py-2.5">
        <span className="text-sm text-[var(--color-text-secondary)]">{t('wizard.device.notEvidenced', { group: label })}</span>
        <Button variant="link" onClick={() => setAdded((s) => ({ ...s, [g]: true }))}>
          {t('wizard.device.addGroup', { group: label })}
        </Button>
      </div>
    );

  return (
    <div className="space-y-5">
      <p className="text-sm text-[var(--color-text-secondary)]">{t('wizard.device.hint')}</p>
      {(Object.keys(DEVICE_CAPABILITY_GROUPS) as DeviceCapabilityGroup[]).map((capGroup) => (
        <fieldset key={capGroup}>
          <legend className="mb-1.5 text-xs font-medium uppercase tracking-wide text-[var(--color-text-secondary)]">
            {t(`wizard.device.groups.${capGroup}`)}
          </legend>
          <div className="grid gap-1.5 sm:grid-cols-2">
            {DEVICE_CAPABILITY_GROUPS[capGroup].map((token) => (
              <Checkbox
                key={token}
                checked={caps.includes(token)}
                onChange={(on) => toggleCap(token, on)}
                label={t(`assets:capability.${token}`)}
              />
            ))}
          </div>
        </fieldset>
      ))}
      <div className="space-y-3 border-t border-[var(--color-border)] pt-4">
        <h3 className="text-xs font-medium uppercase tracking-wide text-[var(--color-text-secondary)]">
          {t('wizard.device.reach')}
        </h3>
        {group(
          'cellular',
          t('wizard.device.groupCellular'),
          <div className="grid gap-4 sm:grid-cols-2">
            {countField('sims', 'pw-sims', t('wizard.device.sims'))}
            <div className="self-end pb-2">
              <Toggle checked={v.esim} onChange={(on) => form.setValue('esim', on, { shouldDirty: true })} label={t('wizard.device.esim')} />
            </div>
          </div>,
        )}
        {group(
          'ethernet',
          t('wizard.device.groupEthernet'),
          <div className="grid gap-4 sm:grid-cols-2">
            {countField('ethernetPorts', 'pw-eth', t('wizard.device.ethernetPorts'))}
            {countField('ethernetWan', 'pw-wan', t('wizard.device.ethernetWan'))}
          </div>,
        )}
        {group(
          'serial',
          t('wizard.device.groupSerial'),
          <div className="grid gap-4 sm:grid-cols-2">
            {countField('rs232', 'pw-232', t('wizard.device.rs232'))}
            {countField('rs485', 'pw-485', t('wizard.device.rs485'))}
          </div>,
        )}
      </div>
    </div>
  );
}

// The REVIEW — the summary step on the shared composite: the steps as sections
// in their order, EVERY fact the wizard asked shown set or "Not set", "Change"
// back to each step. The reach groups read as the device step evidences them:
// a present group its counts, an absent one "Not evidenced" (the absence law —
// ADR-067 rev); the networked switch is structurally absent on a
// connectivity-na category, so nothing of it is said there.
function ReviewStep({ form, onChange }: { form: F; onChange: (step: number) => void }) {
  const { t } = useTranslation('catalog');
  const { t: tc } = useTranslation('common');
  const v = form.getValues();
  const category = v.category as AssetCategory | '';
  if (!category) return null;
  const notSet = tc('wizardReview.notSet');
  const yesNo = (on: boolean) => (on ? tc('value.yes') : tc('value.no'));
  const text = (label: string, value: string, mono = false): WizardReviewRow =>
    value.trim()
      ? {
          label,
          value: (
            <span dir="ltr" className={mono ? 'font-mono' : undefined}>
              {value.trim()}
            </span>
          ),
        }
      : { label, missing: notSet };
  const count = (label: string, n: number | null): WizardReviewRow => (n !== null ? { label, value: formatNumber(n) } : { label, missing: notSet });
  const specRows: WizardReviewRow[] = ASSET_CATEGORY_DESCRIPTORS[category].modelSpecColumns.map((col) => {
    if (col === 'battery_chemistry') {
      const label = t('assets:form.model.chemistry');
      return v.specBatteryChem ? { label, value: t(`assets:form.model.batteryChem.${v.specBatteryChem}`) } : { label, missing: notSet };
    }
    if (col === 'battery_technology') {
      const label = t('assets:form.model.technology');
      return v.specBatteryTech ? { label, value: t(`assets:form.model.batteryTechnology.${v.specBatteryTech}`) } : { label, missing: notSet };
    }
    return count(t(`assets:form.model.spec.${col}`), v[SPEC_FIELD[col].name] as number | null);
  });
  const identity: WizardReviewRow[] = [
    { label: t('wizard.fields.category'), value: t(`assets:category.${category}`) },
    text(t('wizard.fields.name'), v.name),
    text(t('wizard.fields.manufacturer'), v.manufacturer),
    text(t('wizard.fields.series'), v.series),
    text(t('wizard.fields.partNumber'), v.partNumber, true),
    v.description.trim()
      ? { label: t('wizard.fields.description'), value: <span className="whitespace-pre-wrap">{v.description.trim()}</span> }
      : { label: t('wizard.fields.description'), missing: notSet },
    ...specRows,
    count(t('assets:form.model.warrantyMonths'), v.warrantyMonths),
    ...(isConnectivityNa(category)
      ? []
      : [{ label: t('wizard.fields.networked'), value: yesNo(v.networked) }, ...(v.networked ? [{ label: t('wizard.fields.fronts'), value: yesNo(v.fronts) }] : [])]),
  ];
  const caps = (v.capabilities as DeviceCapability[]).map((c) => t(`assets:capability.${c}`));
  const present = {
    cellular: (v.sims !== null && v.sims > 0) || v.esim,
    ethernet: v.ethernetPorts !== null && v.ethernetPorts > 0,
    serial: (v.rs232 !== null && v.rs232 > 0) || (v.rs485 !== null && v.rs485 > 0),
  };
  const notEvidenced = t('wizard.review.notEvidenced');
  const device: WizardReviewRow[] = [
    caps.length > 0 ? { label: t('wizard.review.capabilities'), value: caps.join(' · ') } : { label: t('wizard.review.capabilities'), missing: notSet },
    // The counts as the save keeps them (the same caps `buildDeviceHalf` applies).
    ...(present.cellular
      ? [count(t('wizard.device.sims'), v.sims !== null ? Math.min(v.sims, 4) : null), { label: t('wizard.device.esim'), value: yesNo(v.esim) }]
      : [{ label: t('wizard.device.groupCellular'), missing: notEvidenced }]),
    ...(present.ethernet
      ? [count(t('wizard.device.ethernetPorts'), v.ethernetPorts), count(t('wizard.device.ethernetWan'), v.ethernetWan !== null ? Math.min(v.ethernetWan, v.ethernetPorts ?? 0) : null)]
      : [{ label: t('wizard.device.groupEthernet'), missing: notEvidenced }]),
    ...(present.serial
      ? [count(t('wizard.device.rs232'), v.rs232), count(t('wizard.device.rs485'), v.rs485)]
      : [{ label: t('wizard.device.groupSerial'), missing: notEvidenced }]),
  ];
  // The steps' ids in their order — the sections name their step by id (the device step exists only when networked).
  const stepIds = ['identity', ...(v.networked ? ['device'] : []), 'review'];
  const sections: WizardReviewSection[] = [
    { id: 'identity', title: t('wizard.steps.identity'), step: stepIds.indexOf('identity'), rows: identity },
    ...(v.networked ? [{ id: 'device', title: t('wizard.steps.device'), step: stepIds.indexOf('device'), rows: device }] : []),
  ];
  return <WizardReview sections={sections} onChange={onChange} />;
}

// ── the wizard ───────────────────────────────────────────────────────────────

export interface ProductCreateWizardProps {
  /** The house form body (ADR-038 §2 — useFormShell owns RHF + Zod + the
   *  refusal line + the dirty guard's router-agnostic half). The CONTAINER owns the
   *  route-leave half: the page adds useFormRouteLeave(shell); a Workbench
   *  story builds a shell without it. */
  shell: UseFormShellReturn<ProductFormValues>;
  /** Category-narrowed existing series for the suggest-existing datalist. */
  seriesSuggestions?: string[];
  /** Live variant — the route page derives suggestions per the picked category. */
  seriesFor?: (category: AssetCategory | '') => string[];
  /** Judging affordance (Workbench): land on a step. */
  initialStep?: number;
}

export function ProductCreateWizard({
  shell,
  seriesSuggestions = [],
  seriesFor,
  initialStep,
}: ProductCreateWizardProps) {
  const { t } = useTranslation('catalog');
  const { form } = shell;
  const networked = form.watch('networked');
  const category = form.watch('category');
  const suggestions = seriesFor ? seriesFor(category as AssetCategory | '') : seriesSuggestions;
  // The step is the wizard's (the Stepper controlled): the review's "Change" jumps back to a step through it.
  const [step, setStep] = useState(initialStep ?? 0);

  // The conditional-step law: a non-networked product HAS no capabilities step —
  // the array simply doesn't carry it (the rail renumbers; no skip ceremony).
  const steps: StepperStep[] = useMemo(
    () => [
      {
        id: 'identity',
        label: t('wizard.steps.identity'),
        content: <IdentityStep form={form} seriesSuggestions={suggestions} />,
        // the one fact the form is meaningless without — Next reads disabled
        // until a tile is picked (visual truth, not a validation surprise)
        nextDisabled: !category,
      },
      ...(networked
        ? [{ id: 'device', label: t('wizard.steps.device'), content: <ProductDeviceFields form={form} /> } satisfies StepperStep]
        : []),
      { id: 'review', label: t('wizard.steps.review'), content: <ReviewStep form={form} onChange={setStep} /> },
    ],
    [t, form, networked, category, suggestions],
  );

  return (
    <div className="mx-auto w-full max-w-3xl px-6 py-8">
      <h1 className="mb-2 text-lg font-bold text-[var(--color-text-primary)]">{t('wizard.title')}</h1>
      <p className="text-sm text-[var(--color-text-secondary)]">{t('wizard.caption')}</p>
      <form id={shell.formId} onSubmit={shell.onFormSubmit} noValidate className="mt-6">
        <RefusalLine line={shell.line} className="mb-4" />
        <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-surface)] p-6">
          <Stepper
            steps={steps}
            step={step}
            onStepChange={setStep}
            ariaLabel={t('wizard.stepperAria')}
            onValidateStep={(i) => (steps[i]?.id === 'identity' ? form.trigger(['category', 'name']) : true)}
            onSubmit={() => void shell.onFormSubmit()}
            submitLabel={t('wizard.submit')}
            isSubmitting={shell.isSubmitting}
          />
        </div>
      </form>
      {shell.discardDialog}
    </div>
  );
}

export default ProductCreateWizard;
